'use client';
import { useCallback, useMemo, useRef } from 'react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';

import { useSportsSidebarData } from '@/services/query';
import { SidebarItem, SportsLeague, SportsSidebarCategory } from '@/types';

import { ROUTES } from '@/constants';

/**
 * Type guards and helpers for sports sidebar items
 */
export const isLeague = (item: SidebarItem | null): item is SportsLeague =>
  !!item && 'id' in item && typeof item.id === 'number' && !('type' in item);

export const isAutomated = (
  item: SidebarItem | null
): item is SportsSidebarCategory & { type: 'automated' } =>
  !!item && 'type' in item && item.type === 'automated';

export const isNonAutomated = (
  item: SidebarItem | null
): item is SportsSidebarCategory & { type: 'non-automated' } =>
  !!item && 'type' in item && item.type === 'non-automated';

export const hasSubLeagues = (item: SidebarItem | null): boolean =>
  isAutomated(item) && item.leagues.length > 1;

export const checkIsCategoryActive = (
  category: SportsSidebarCategory,
  activeId: string | number | null
): boolean => {
  if (activeId === category.category) return true;
  if (isAutomated(category)) {
    return category.leagues.some((l) => String(l.id) === String(activeId));
  }
  return false;
};

/**
 * Normalizes a slug or string for comparison (lowercase and replaces spaces/dashes)
 */
const normalizeMatchString = (str: string | undefined | null) =>
  String(str || '')
    ?.toLowerCase()
    .replace(/[\s-]+/g, '')
    .trim();

/**
 * Custom hook for managing the Sports Sidebar logic, including
 * item selection, navigation, and URL synchronization.
 */
export const useSportsSidebar = () => {
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();

  const sportSlug = (params.slug as string)?.toLowerCase();
  const gameId = params.id as string;
  const urlId = searchParams.get('id');

  // --- Refs ---
  const mobileSubCategoryRef = useRef<HTMLDivElement>(null);
  const categoryButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // --- Data Fetching ---
  const {
    data: sidebarResponseData,
    isLoading: isSidebarLoading,
    isFetching: isSidebarFetching,
    isError,
    refetch,
  } = useSportsSidebarData();

  const allAutomatedItems = sidebarResponseData?.automated || [];
  const allNonAutomatedItems = sidebarResponseData?.nonAutomated || [];

  const allSidebarItems = [...allAutomatedItems, ...allNonAutomatedItems];

  /**
   * Utility to get the unique display ID (tag or id) for a sidebar item.
   * Categories are identified by their Name, Leagues by their numeric ID.
   */
  const getActiveId = (item: SidebarItem | null): string | number | null => {
    if (!item) return null;
    if (isLeague(item)) return item.id;
    if (isNonAutomated(item)) return item.category; // Use name for category
    if (isAutomated(item)) return item.category; // Use name for category
    return null;
  };

  const findActiveSidebarItem = (): SidebarItem | null => {
    if (!sidebarResponseData) return null;

    if (!sportSlug) return null;

    // 1. Try matching by ID first (most deterministic)
    if (urlId) {
      for (const item of allSidebarItems) {
        const itemId = String(item.id);
        const itemActiveId = String(getActiveId(item));

        // Check top-level category by Name OR numeric internal ID
        if (itemActiveId === urlId || itemId === urlId) {
          return item;
        }

        // Check child leagues for automated categories
        if (isAutomated(item)) {
          const foundLeague = item.leagues.find((l) => String(l.id) === urlId);
          if (foundLeague) return foundLeague;
        }
      }
    }

    // 2. Fallback to slug matching with Strict Priority (Category > League)
    if (sportSlug) {
      const normalizedSportSlug = normalizeMatchString(sportSlug);

      if (normalizedSportSlug === 'live') return null; // Let the hook handling handle live

      // Pass 1: Match top-level categories ONLY
      for (const item of allSidebarItems) {
        const rawSlug = isNonAutomated(item)
          ? item.leagues?.slug
          : item.sport || item.category || '';
        const itemSlug = normalizeMatchString(rawSlug);

        if (itemSlug === normalizedSportSlug) {
          return item;
        }
      }

      // Pass 2: Match sub-leagues ONLY if no category matched
      for (const item of allSidebarItems) {
        if (isAutomated(item)) {
          const foundLeague = item.leagues.find(
            (l) => normalizeMatchString(l.sport) === normalizedSportSlug
          );
          if (foundLeague) return foundLeague;
        }
      }
    }

    return null;
  };

  const currentCategoryData = findActiveSidebarItem();

  const setCategoryButtonRef = (id: string, el: HTMLButtonElement | null) => {
    categoryButtonRefs.current[id] = el;
  };

  const handleCategoryClick = (category: SidebarItem) => {
    let targetSlug = (category as any).sport || '';
    let targetId: string | number | undefined;

    if (isLeague(category)) {
      targetSlug = category.sport;
      targetId = category.id;
    } else if (isNonAutomated(category)) {
      targetSlug = category.leagues?.tag || '';
      targetId = category.category;
    } else if (isAutomated(category)) {
      targetSlug = category.sport || category.category?.toLowerCase();
      targetId = category.category;
    } else if (category && 'category' in category) {
      targetSlug = targetSlug || (category as any).category?.toLowerCase() || '';
    }

    // 2. Normalize and check for special navigation cases
    const normalizedSlug = targetSlug?.toLowerCase();

    // 3. Handle mobile scroll centering (UI only)
    const clickedButton = categoryButtonRefs.current[normalizedSlug];
    if (clickedButton && mobileSubCategoryRef.current) {
      const container = mobileSubCategoryRef.current;
      const buttonRect = clickedButton.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const scrollLeft =
        container.scrollLeft +
        (buttonRect.left - containerRect.left) -
        containerRect.width / 2 +
        buttonRect.width / 2;

      container.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth',
      });
    }

    // 4. Execute Navigation
    if (normalizedSlug === 'live' || !normalizedSlug) {
      router.push(ROUTES.SPORTS);
    } else if (normalizedSlug === 'futures') {
      router.push('/sports/futures');
    } else {
      const url = targetId
        ? `/sports/${normalizedSlug}?id=${targetId}`
        : `/sports/${normalizedSlug}`;
      router.push(url);
    }
  };

  const activeId = getActiveId(currentCategoryData);

  return {
    state: {
      sidebarData: sidebarResponseData,
      allItems: allSidebarItems,
      currentCategoryData,
      activeId,
      isLoading: isSidebarLoading,
      isFetching: isSidebarFetching,
      isError,
      sportSlug: sportSlug || 'live',
      gameId,
    },
    handlers: {
      handleCategoryClick,
      setCategoryButtonRef,
      refetch,
    },
    refs: {
      mobileSubCategoryRef,
      categoryButtonRefs,
    },
  };
};
